import { useMemo, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { ArrowLeft, Printer } from 'lucide-react'

import { useEvent } from '@/stores/events.store'
import { useBooking } from '@/stores/bookings.store'
import { useEventTimeline } from '@/stores/event-timeline.store'
import { useEventPackingList } from '@/stores/event-packing.store'
import { listEventAddons, listJobEvents } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListEventAddonsRequestSchema } from '@/gen/cortado/v1/event_plan_pb'
import { ListJobEventsRequestSchema } from '@/gen/cortado/v1/jobs_pb'
import { OrganizationFeatureEnum } from '@/gen/cortado/v1/settings_pb'
import { requireOrganizationFeature } from '@/lib/route-preloading'
import { formatDateTimeRange } from '@/lib/date-utils'
import { buildRunSheet } from '@/lib/run-sheet'
import { formatFullName } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { jobDetailEventTitle } from '@/components/pipeline/job-detail-view'

// `$eventId_` keeps this a page of its own rather than an outlet of the event page.
export const Route = createFileRoute('/_app/_home/pipeline/$id_/events/$eventId_/run-sheet')({
  component: RunSheetRouteFromParams,
  // Without the Pipeline, the event still has its standalone page.
  beforeLoad: ({ context, params }) =>
    requireOrganizationFeature(context, OrganizationFeatureEnum.ORGANIZATION_FEATURE_JOBS, () =>
      redirect({ to: '/events/$id', params: { id: params.eventId }, replace: true }),
    ),
})

function RunSheetRouteFromParams() {
  const { id, eventId } = Route.useParams()
  return <RunSheetPage jobId={id} eventId={eventId} />
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="break-inside-avoid-page">
      <h2 className="mb-2 border-b pb-1 text-sm font-semibold tracking-wide uppercase">{title}</h2>
      {children}
    </section>
  )
}

/**
 * The printable run sheet for one booked event: when and where, the client
 * contact, staff, the run of show, add-ons, the packing list with boxes to
 * tick, and on-site details from the questionnaire. It opens over the app
 * and prints on its own.
 */
export function RunSheetPage({ jobId, eventId }: { jobId: string; eventId: string }) {
  const { event, isLoading: isEventLoading, isError: isEventError } = useEvent(eventId)
  const { booking } = useBooking(event?.bookingId)
  const { timeline, isLoading: isTimelineLoading } = useEventTimeline(eventId)
  const { packingList, isLoading: isPackingLoading } = useEventPackingList(eventId)
  const eventsRequest = useMemo(() => create(ListJobEventsRequestSchema, { jobId }), [jobId])
  const eventsQuery = useQuery(listJobEvents, eventsRequest, { enabled: Boolean(jobId) })
  const addonsRequest = useMemo(() => create(ListEventAddonsRequestSchema, { eventId }), [eventId])
  const addonsQuery = useQuery(listEventAddons, addonsRequest, { enabled: Boolean(eventId) })

  const lite = eventsQuery.data?.events.find((row) => row.id === eventId)
  const sheet = buildRunSheet({
    staff: lite?.staff,
    timeline,
    timeZone: event?.eventTimezone,
    packingItems: packingList?.items,
    addons: addonsQuery.data?.addons,
    questionnaire: booking?.questionnaire,
  })

  const loading = isEventLoading || isTimelineLoading || isPackingLoading
  const title = lite ? jobDetailEventTitle(lite) : event?.eventTypeKey || 'Event'
  const contact = event?.contact
  const contactLine = contact
    ? [formatFullName(contact.firstName, contact.lastName, ''), contact.phoneNumber, contact.email].filter(Boolean).join(' · ')
    : ''

  const body = (
    // Print rules for data-run-sheet-root / data-run-sheet-toolbar live in styles.css.
    <div
      data-run-sheet-root
      className="fixed inset-0 z-50 overflow-y-auto bg-background text-foreground print:bg-white print:text-black"
    >
      <div data-run-sheet-toolbar className="sticky top-0 flex items-center justify-between gap-2 border-b bg-background px-4 py-2">
        <Button asChild size="sm" variant="ghost">
          <Link to="/pipeline/$id/events/$eventId" params={{ id: jobId, eventId }}>
            <ArrowLeft data-icon="inline-start" />
            Back to event
          </Link>
        </Button>
        <Button size="sm" onClick={() => window.print()} disabled={loading || !event}>
          <Printer data-icon="inline-start" />
          Print
        </Button>
      </div>

      <article aria-label="Run sheet" className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-8 text-sm">
        {loading ? (
          <div className="flex flex-col gap-3">
            <SkeletonBar className="h-8 w-64" />
            <SkeletonBar className="h-40 w-full" />
          </div>
        ) : isEventError || !event ? (
          <p>We couldn't load this event.</p>
        ) : (
          <>
            <header className="flex flex-col gap-1">
              <p className="text-xs tracking-widest text-muted-foreground uppercase">Run sheet</p>
              <h1 className="text-2xl font-semibold">{title}</h1>
              <p>
                {[
                  formatDateTimeRange(event.eventDate, event.eventLength, event.eventTimezone) || 'Date TBD',
                  event.eventGuests ? `${event.eventGuests} ${event.eventGuests === 1 ? 'guest' : 'guests'}` : '',
                  event.baristaCount ? `${event.baristaCount} ${event.baristaCount === 1 ? 'barista' : 'baristas'}` : '',
                  event.cartCount ? `${event.cartCount} ${event.cartCount === 1 ? 'cart' : 'carts'}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
              {event.eventLocation && <p>{event.eventLocation}</p>}
              {contactLine && <p>Contact: {contactLine}</p>}
            </header>

            <Section title="Staff">
              {sheet.staff.length === 0 ? (
                <p className="text-muted-foreground">No one assigned yet.</p>
              ) : (
                <ul className="flex flex-col gap-0.5">
                  {sheet.staff.map((person, index) => (
                    <li key={`${person.name}-${index}`}>
                      <span className="font-medium">{person.role}:</span> {person.name}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            <Section title="Run of show">
              {sheet.timeline.length === 0 ? (
                <p className="text-muted-foreground">No timeline saved yet.</p>
              ) : (
                <table className="w-full">
                  <tbody>
                    {sheet.timeline.map((block, index) => (
                      <tr key={`${block.label}-${index}`} className="align-top">
                        <td className="w-44 py-0.5 pr-4 font-financial whitespace-nowrap">{block.time}</td>
                        <td className="py-0.5">
                          {block.label}
                          {block.note && <span className="text-muted-foreground"> · {block.note}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Section>

            {sheet.addons.length > 0 && (
              <Section title="Add-ons">
                <p>{sheet.addons.join(', ')}</p>
              </Section>
            )}

            <Section title="Packing list">
              {sheet.packing.length === 0 ? (
                <p className="text-muted-foreground">Nothing on the packing list yet.</p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 print:grid-cols-2">
                  {sheet.packing.map((group) => (
                    <div key={group.category} className="break-inside-avoid">
                      <h3 className="mb-1 font-medium">{group.category}</h3>
                      <ul className="flex flex-col gap-1">
                        {group.lines.map((line) => (
                          <li key={line.id} className="flex items-start gap-2">
                            <span
                              aria-label={line.packed ? 'Packed' : 'Not packed'}
                              role="img"
                              className="mt-0.5 flex size-4 shrink-0 items-center justify-center border border-foreground text-xs leading-none"
                            >
                              {line.packed ? '✓' : ''}
                            </span>
                            <span>
                              <span className="font-financial">{line.quantity}</span> {line.name}
                              {line.unit && <span className="text-muted-foreground"> · {line.unit}</span>}
                              {line.detail && <span className="block text-xs text-muted-foreground">{line.detail}</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {sheet.answers.length > 0 && (
              <Section title="On-site details">
                <dl className="flex flex-col gap-1.5">
                  {sheet.answers.map((row, index) => (
                    <div key={`${row.question}-${index}`}>
                      <dt className="font-medium">{row.question}</dt>
                      <dd>{row.answer}</dd>
                    </div>
                  ))}
                </dl>
              </Section>
            )}
          </>
        )}
      </article>
    </div>
  )

  return typeof document === 'undefined' ? body : createPortal(body, document.body)
}
